import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useCamera, type CameraController } from "../hooks/useCamera";
import { useMediaQuery, PHONE_QUERY } from "../hooks/useMediaQuery";

/**
 * 키오스크(iPad) 프리뷰 확대 배율 (BOOTH-23). 16:9 스트림을 3:4 뷰포트에 cover 로 넣으면 세로가 프레임 전체라
 * 얼굴이 작게 보인다 — 중앙을 이 배율로 키워 얼굴을 크게. capture 도 같은 배율로 중앙을 잘라 결과가 일치한다.
 * 폰은 팔 길이에서 셀피를 찍어 이미 얼굴이 크므로 1 (PR #13 레이아웃 유지).
 * 스트림을 4:3 최대 해상도로 바꾼 뒤에도(useCamera CONSTRAINTS) 폭 기준 FOV 는 같아 배율은 그대로 둔다 —
 * 이 배율만큼 픽셀을 버리는 건 여전하므로(1/1.35² ≈ 55%), 더 줄이려면 UX 결정(얼굴 크기)과 함께 조정.
 */
export const KIOSK_CAMERA_ZOOM = 1.35;

interface SlotRect {
  top: number;
  left: number;
  width: number;
  height: number;
  /**
   * 슬롯이 스크롤 컨테이너(.view) 밖으로 나간 부분을 잘라내는 clip-path.
   * <video> 는 fixed 라 컨테이너의 overflow 클리핑을 받지 않아, 폰에서 스크롤하면 헤더·진행 표시 위로
   * 영상이 그대로 그려진다 — 컨테이너 rect 와의 교집합만 보이게 한다. 잘릴 게 없으면 undefined.
   */
  clipPath?: string;
  /** 슬롯의 border-radius (computed) — 래퍼가 같은 모양으로 잘리게 */
  borderRadius?: string;
}

/** 슬롯과 가장 가까운 스크롤 컨테이너 rect 의 교집합으로 inset() 을 만든다 */
function clipToScroller(el: HTMLElement, r: DOMRect): string | undefined {
  const scroller = el.closest<HTMLElement>(".view");
  if (!scroller) return undefined;
  const s = scroller.getBoundingClientRect();
  const top = Math.max(0, s.top - r.top);
  const bottom = Math.max(0, r.bottom - s.bottom);
  const left = Math.max(0, s.left - r.left);
  const right = Math.max(0, r.right - s.right);
  if (!top && !bottom && !left && !right) return undefined;
  return `inset(${top}px ${right}px ${bottom}px ${left}px)`;
}

interface CameraContextValue extends CameraController {
  /** 현재 프리뷰가 채워야 할 화면상 rect. null이면 <video> 숨김. */
  setSlot: (el: HTMLElement | null) => void;
}

const CameraContext = createContext<CameraContextValue | null>(null);

export function useCameraContext(): CameraContextValue {
  const ctx = useContext(CameraContext);
  if (!ctx) throw new Error("useCameraContext: CameraProvider 밖에서 호출됨");
  return ctx;
}

export function CameraProvider({ children }: { children: ReactNode }) {
  const camera = useCamera();
  const isPhone = useMediaQuery(PHONE_QUERY);
  const zoom = isPhone ? 1 : KIOSK_CAMERA_ZOOM;
  // 프리뷰와 같은 배율로 캡처 — 소비자(PreviewView)는 배율을 몰라도 된다
  const { capture: rawCapture } = camera;
  const capture = useCallback(() => rawCapture(zoom), [rawCapture, zoom]);
  const [rect, setRect] = useState<SlotRect | null>(null);
  const [slotVersion, setSlotVersion] = useState(0);
  const slotElRef = useRef<HTMLElement | null>(null);

  const recompute = useCallback(() => {
    const el = slotElRef.current;
    if (!el) {
      setRect(null);
      return;
    }
    const r = el.getBoundingClientRect();
    setRect({
      top: r.top, left: r.left, width: r.width, height: r.height,
      clipPath: clipToScroller(el, r),
      borderRadius: getComputedStyle(el).borderRadius,
    });
  }, []);

  const setSlot = useCallback(
    (el: HTMLElement | null) => {
      slotElRef.current = el;
      recompute();
      if (el) setSlotVersion((v) => v + 1);
    },
    [recompute]
  );

  // 슬롯 element가 바뀔 때마다 observer 재설정
  useEffect(() => {
    const el = slotElRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => recompute());
    ro.observe(el);
    const onResize = () => recompute();
    const onScroll = () => recompute();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [recompute, slotVersion]);

  // Provider 언마운트 시 스트림 정리
  useEffect(() => {
    return () => camera.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<CameraContextValue>(
    () => ({ ...camera, capture, setSlot }),
    [
      camera.status,
      camera.errorKind,
      camera.videoRef,
      camera.start,
      camera.stop,
      capture,
      setSlot,
    ]
  );

  const videoVisible = camera.status === "ready" && rect !== null;
  // 확대는 <video> 를 슬롯보다 zoom 배 크게 그리고 슬롯 크기의 래퍼가 넘친 부분을 잘라내는 방식 —
  // clip-path 는 transform 전에 적용돼 scale 로는 슬롯 밖으로 넘친다.
  const overflow = `${((zoom - 1) / 2) * 100}%`;

  return (
    <CameraContext.Provider value={value}>
      {children}
      <div
        aria-hidden
        style={{
          position: "fixed",
          top: rect?.top ?? 0,
          left: rect?.left ?? 0,
          width: rect?.width ?? 0,
          height: rect?.height ?? 0,
          overflow: "hidden",
          clipPath: rect?.clipPath,
          borderRadius: rect?.borderRadius ?? 0,
          pointerEvents: "none",
          zIndex: 1,
          // display 토글 대신 opacity 전환 — 스테이지 페이드(0.35s)와 맞춰 영상이 튀어나오거나 뚝 끊기지 않게
          opacity: videoVisible ? 1 : 0,
          visibility: videoVisible ? "visible" : "hidden",
          transition: "opacity 0.35s ease, visibility 0.35s ease",
        }}
      >
        <video
          ref={camera.videoRef}
          autoPlay
          muted
          playsInline
          style={{
            position: "absolute",
            top: `-${overflow}`,
            left: `-${overflow}`,
            width: `${zoom * 100}%`,
            height: `${zoom * 100}%`,
            // Tailwind preflight 의 `img, video { max-width: 100% }` 가 확대 폭을 눌러 세로만 커지던 문제 — 해제
            maxWidth: "none",
            maxHeight: "none",
            objectFit: "cover",
            transform: "scaleX(-1)",
          }}
        />
      </div>
    </CameraContext.Provider>
  );
}
